import { GlobalContext } from 'context';
import React, { ReactElement, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useDispatchState from 'shared/hooks/useDispatchState';
import { workplaceDescription, workplaceOf } from 'shared/utils/workplace';
import { menuUrlOf } from './active-menu';
import SidebarStyled from './Sidebar.styled';

const { ActiveWorkplace, ActiveCompanyName, ActiveBranchOfficeName } = SidebarStyled;

const Sidebar = (): ReactElement => {
  const [{ accessUser, branchOfficeSelected }] = useDispatchState(GlobalContext);
  const navigateRoute = useNavigate();
  const { pathname } = useLocation();
  const workplace = branchOfficeSelected?.name ? workplaceOf(branchOfficeSelected) : null;

  const setContentHeight = (): void => {
    const rightCol = document.querySelector('.right_col') as HTMLElement;
    const body = document.body;
    const footer = document.querySelector('footer') as HTMLElement;
    const leftCol = document.querySelector('.left_col') as HTMLElement;
    const sidebarFooter = document.querySelector('.sidebar-footer') as HTMLElement;
    const navMenu = document.querySelector('.nav_menu') as HTMLElement;

    if (!rightCol || !body || !footer || !leftCol || !navMenu) return;

    rightCol.style.minHeight = `${window.innerHeight}px`;

    const bodyHeight = body.offsetHeight;
    const footerHeight = body.classList.contains('footer_fixed') ? -10 : footer.offsetHeight;
    const leftColHeight = leftCol.offsetHeight + (sidebarFooter?.offsetHeight ?? 0);
    let contentHeight = Math.max(bodyHeight, leftColHeight);

    contentHeight -= navMenu.offsetHeight + footerHeight;

    rightCol.style.minHeight = `${contentHeight}px`;
  };

  const openUpMenu = (): void => {
    const sidebarMenu = document.getElementById('sidebar-menu') as HTMLElement;
    sidebarMenu.querySelectorAll('li').forEach((li) => {
      li.classList.remove('active', 'active-sm');
    });
    sidebarMenu.querySelectorAll('li ul').forEach((ul) => {
      (ul as HTMLElement).style.display = 'none';
    });
  };

  const handleMenuClick = (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    const li = ev.currentTarget.parentElement as HTMLElement;
    const sidebarMenu = document.getElementById('sidebar-menu') as HTMLElement;

    sidebarMenu.querySelectorAll('.child_menu li').forEach((item) => {
      if (item !== li) {
        item.classList.remove('active');
      }
    });

    if (li.classList.contains('active')) {
      li.classList.remove('active', 'active-sm');
      const ul = li.querySelector('ul:first-of-type') as HTMLElement;
      if (ul) {
        ul.style.display = 'none';
        setContentHeight();
      }
    } else {
      if (!li.parentElement?.classList.contains('child_menu')) {
        openUpMenu();
      } else {
        if (document.body.classList.contains('nav-sm')) {
          if (!li.parentElement?.classList.contains('child_menu')) {
            openUpMenu();
          }
        }
      }

      li.classList.add('active');
      const ul = li.querySelector('ul:first-of-type') as HTMLElement;
      if (ul) {
        ul.style.display = 'block';
        setContentHeight();
      }
    }
  };

  useEffect(() => {
    setContentHeight();
    window.addEventListener('resize', setContentHeight);

    return () => {
      window.removeEventListener('resize', setContentHeight);
    };
  }, []);

  useEffect(() => {
    const sidebarMenu = document.getElementById('sidebar-menu');
    const anchors = sidebarMenu?.querySelectorAll('a');
    anchors?.forEach((anchor) => {
      anchor.addEventListener('click', handleMenuClick as unknown as EventListener);
    });

    return () => {
      anchors?.forEach((anchor) => {
        anchor.removeEventListener('click', handleMenuClick as unknown as EventListener);
      });
    };
  }, [accessUser]);

  const highlightMenuOf = (menuUrl: string): void => {
    const sidebarMenu = document.getElementById('sidebar-menu');
    if (!sidebarMenu) return;

    const isCollapsed = document.body.classList.contains('nav-sm');
    sidebarMenu.querySelectorAll('li').forEach((li) => li.classList.remove('active', 'active-sm'));
    sidebarMenu.querySelectorAll<HTMLElement>('.child_menu').forEach((childMenu) => {
      childMenu.style.display = 'none';
    });

    const currentItem = Array.from(sidebarMenu.querySelectorAll<HTMLElement>('li[data-menu-url]')).find(
      (item) => item.dataset.menuUrl === menuUrl,
    );
    const moduleItem = currentItem?.parentElement?.closest('li');
    if (!currentItem || !moduleItem) return;

    currentItem.classList.add('active');
    moduleItem.classList.add(isCollapsed ? 'active-sm' : 'active');
    if (isCollapsed) return;

    const childMenu = moduleItem.querySelector<HTMLElement>('.child_menu');
    if (childMenu) childMenu.style.display = 'block';
    setContentHeight();
  };

  useEffect(() => {
    highlightMenuOf(menuUrlOf(pathname));
  }, [pathname, accessUser]);

  const onNavigate = (url: string): void => {
    navigateRoute(url);

    if (document.body.classList.contains('nav-sm')) {
      const sidebarMenu = document.getElementById('sidebar-menu');
      sidebarMenu?.querySelectorAll('li ul').forEach((ul) => {
        (ul as HTMLElement).style.display = 'none';
      });
      sidebarMenu?.querySelectorAll('li').forEach((li) => {
        li.classList.remove('active', 'active-sm');
      });

      if (window.innerWidth <= 991) {
        document.getElementById('menu_toggle')?.click();
      }
    }
  };

  return (
    <div className="col-md-3 left_col menu_fixed">
      <div className="left_col scroll-view">
        <div className="navbar nav_title" style={{ border: 0 }}>
          <a className="site_title">
            <img src="/build/images/NuvySoftIcono.png" alt="NuvySoft" style={{ width: '45px', height: 'auto' }} />
            <span>NuvySoft</span>
          </a>
        </div>
        <div className="clearfix" />
        {workplace && (
          <ActiveWorkplace title={workplaceDescription(workplace)}>
            <ActiveCompanyName>
              <span className="sr-only">Empresa: </span>
              {workplace.companyName}
            </ActiveCompanyName>
            {workplace.branchOfficeName && (
              <ActiveBranchOfficeName>
                <i className="fa fa-map-marker mr-1" aria-hidden="true" />
                <span className="sr-only">Sucursal: </span>
                {workplace.branchOfficeName}
              </ActiveBranchOfficeName>
            )}
          </ActiveWorkplace>
        )}
        <div id="sidebar-menu" className="main_menu_side hidden-print main_menu">
          <div className="menu_section">
            <ul className="nav side-menu">
              {accessUser?.modules.map(({ icon, id: moduleId, menu, name: moduleName }) => (
                <li key={moduleId}>
                  <a>
                    <i className={icon} /> {moduleName} <span className="fa fa-chevron-down" />
                  </a>
                  <ul className="nav child_menu">
                    {menu.map(({ id, name, url }) => (
                      <li key={id} data-menu-url={url}>
                        <a onClick={() => onNavigate(url)}>{name}</a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
